import { useCallback } from "react";
import { swapBefundBlock } from "../utils/befundHelpers";
import { replaceRichTextLine } from "../utils/richTextHelpers";

export default function useFieldSelectors({
  fields,
  setFields,
  vorlagen,
  getCustomPrompt,
  getModalitaetenBefundLine,
  getRegionDisplayLabel,
  setIsModalityDropdownOpen,
  setIsRegionDropdownOpen,
  setIsGenderDropdownOpen,
  showStatus,
}) {
  const getVolumetriePrompt = useCallback(
    (name) => getCustomPrompt(name, "Volumetrie"),
    [getCustomPrompt]
  );

  const selectModality = useCallback((mod) => {
    setIsModalityDropdownOpen(false);
    const newMod = fields.modalitat === mod ? "" : mod;
    const oldModLine = getModalitaetenBefundLine(fields.modalitat, { allowLegacyDefault: true });
    const newModLine = getModalitaetenBefundLine(newMod, { allowLegacyDefault: false });
    const richBefund = replaceRichTextLine(fields.befund, oldModLine, newModLine);
    let befund = richBefund ?? fields.befund;
    if (richBefund !== null) {
      setFields(f => ({ ...f, modalitat: newMod, region: "", befund }));
      if (newMod && newModLine) showStatus(`Geräteinformation für ${newMod} eingefügt`);
      return;
    }
    if (oldModLine && befund.includes(oldModLine)) befund = befund.replace(oldModLine + "\n", "").replace(oldModLine, "");
    if (newModLine) {
      const lines = befund.split("\n");
      const isHeaderLine = (line) => line.startsWith("Klinische Angaben:") || line.startsWith("Fragestellung:");
      const findInsertAfterSection = (sectionName) => {
        const header = `${sectionName}:`;
        const startIndex = lines.findIndex((line) => line.startsWith(header));
        if (startIndex === -1) return -1;

        let nextIndex = startIndex + 1;
        while (nextIndex < lines.length) {
          const currentLine = String(lines[nextIndex] || "");
          if (!currentLine.trim()) break;
          if (isHeaderLine(currentLine)) break;
          nextIndex += 1;
        }

        return nextIndex;
      };

      const afterFragestellung = findInsertAfterSection("Fragestellung");
      const afterKlinischeAngaben = findInsertAfterSection("Klinische Angaben");
      const insertAt = afterFragestellung !== -1
        ? afterFragestellung
        : afterKlinischeAngaben !== -1
          ? afterKlinischeAngaben
          : 0;

      const previousLine = insertAt > 0 ? String(lines[insertAt - 1] || "") : "";
      const shouldInsertSpacerBeforeModLine = insertAt > 0 && previousLine.trim() !== "";
      lines.splice(insertAt, 0, ...(shouldInsertSpacerBeforeModLine ? ["", newModLine] : [newModLine]));
      befund = lines.join("\n");
    }
    setFields(f => ({ ...f, modalitat: newMod, region: "", befund }));
    if (newMod && newModLine) showStatus(`Geräteinformation für ${newMod} eingefügt`);
  }, [fields.befund, fields.modalitat, getModalitaetenBefundLine, setFields, setIsModalityDropdownOpen, showStatus]);

  const selectRegion = useCallback((region) => {
    setIsRegionDropdownOpen(false);
    const newRegion = fields.region === region ? "" : region;
    const oldCustom = fields.region ? getCustomPrompt(fields.region, fields.modalitat) : null;
    const newCustom = newRegion ? getCustomPrompt(newRegion, fields.modalitat) : null;
    const oldBlock = oldCustom ? oldCustom.befund : null;
    const oldBeurteilungBlock = oldCustom ? (oldCustom.beurteilung || null) : null;
    const newBlock = newCustom ? newCustom.befund : null;
    const newBeurteilungBlock = newCustom ? (newCustom.beurteilung || null) : null;

    let updatedBefund = fields.befund;
    let updatedBeurteilung = fields.beurteilung;
    if (oldBlock) updatedBefund = swapBefundBlock(updatedBefund, oldBlock, "");
    if (oldBeurteilungBlock) updatedBeurteilung = swapBefundBlock(updatedBeurteilung, oldBeurteilungBlock, "");
    if (newBlock) updatedBefund = swapBefundBlock(updatedBefund, "", newBlock);
    if (newBeurteilungBlock) updatedBeurteilung = swapBefundBlock(updatedBeurteilung, "", newBeurteilungBlock);

    setFields(f => ({ ...f, region: newRegion, befund: updatedBefund, beurteilung: updatedBeurteilung }));
    if (newRegion && newBlock) showStatus(`Vorlage für "${getRegionDisplayLabel(newRegion, fields.modalitat)}" eingefügt`);
  }, [fields.befund, fields.beurteilung, fields.modalitat, fields.region, getCustomPrompt, getRegionDisplayLabel, setFields, setIsRegionDropdownOpen, showStatus]);

  const selectGeschlecht = useCallback((g) => {
    setIsGenderDropdownOpen(false);
    const newG = fields.geschlecht === g ? "" : g;
    const oldCustomV = fields.geschlecht ? getVolumetriePrompt(fields.geschlecht) : null;
    const newCustomV = newG ? getVolumetriePrompt(newG) : null;
    const oldBlock = oldCustomV ? oldCustomV.befund : null;
    const newBlock = newCustomV ? newCustomV.befund : null;
    setFields(f => ({ ...f, geschlecht: newG, befund: swapBefundBlock(f.befund, oldBlock || "", newBlock || "") }));
    if (newG) showStatus(`Volumetrie ${newG} eingefügt`);
  }, [fields.geschlecht, getVolumetriePrompt, setFields, setIsGenderDropdownOpen, showStatus]);

  return {
    selectModality,
    selectRegion,
    selectGeschlecht,
    getVolumetriePrompt,
  };
}
